import * as React from "react"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

import { useTocShowTitle } from "@/tiptap/nodes/toc-node/ui/toc-show-title-button/toc-show-title"

import { Button } from "@/tiptap/ui-primitive/button"

/**
 * Button component for toggling TOC title in a Tiptap editor.
 * Only appears when a TOC node is selected in the editor.
 *
 * For custom button implementations, use the `useTocShowTitle` hook instead.
 */
export const TocShowTitleButton = React.forwardRef((
  {
    editor: providedEditor,
    text,
    hideWhenUnavailable = false,
    onToggle,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)

  const { isVisible, isActive, canToggle, handleToggle, label, Icon } =
    useTocShowTitle({
      editor,
      hideWhenUnavailable,
      onToggle,
    })

  const handleClick = React.useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleToggle()
  }, [handleToggle, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type="button"
      variant="ghost"
      data-active-state={isActive ? "on" : "off"}
      role="button"
      tabIndex={-1}
      disabled={!canToggle}
      data-disabled={!canToggle}
      aria-label={label}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <Icon className="tiptap-button-icon" />

          {text && <span className="tiptap-button-text">{text}</span>}
        </>
      )}
    </Button>
  );
})

TocShowTitleButton.displayName = "TocShowTitleButton"
